import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Box, Column, DropdownMenu, DropdownMenuItem, IconButton, Row, Typography } from 'seagull';
import { Edit, Email, Kebab, Phone, Trash, UserSquare } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import { useIsContactEmailUpdateInProgress } from '~/hooks/usePendingContactEmailUpdates';
import { useLocaleKeys } from '~/i18n';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { contactRoleTextsPath, toContactRoleLabel } from '~/utils/contact-role-labels';
import styles from './ContactItem.module.scss';
import { deleteBlockedTextsPath, deleteTooltipProps, hasPortalAccess } from './ContactItem.utils';
export const GetContactItemTID = (parentTestId)=>getTestIds(parentTestId);
export function ContactItem(param) {
    let { contact, mappings, testId, isArchived, onEdit, onChangeRole, onDelete } = param;
    var _contact_customerId, _contact_userId;
    const { ContactItemTestIds: TID } = useMemo(()=>GetContactItemTID(testId), [
        testId
    ]);
    const { t: contactsTexts } = useLocaleKeys('pages.clientDetail.summary.contacts');
    const { t: roleTexts } = useLocaleKeys(contactRoleTextsPath);
    const { t: deleteBlockedTexts } = useLocaleKeys(deleteBlockedTextsPath);
    const { t: clientViewTexts } = useLocaleKeys('components.clientView');
    const isEmailUpdateInProgress = useIsContactEmailUpdateInProgress(contact.userId());
    const phone = contact.phone();
    const role = contact.role();
    const canReachPortal = hasPortalAccess(contact);
    return /*#__PURE__*/ _jsx(Box, {
        testId: TID.Root(),
        className: styles.row,
        children: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            alignItems: "start",
            justifyContent: "space-between",
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 16,
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 4,
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "normal",
                                    children: contact.fullName()
                                }),
                                mappings.map((mapping)=>/*#__PURE__*/ _jsx(AccountingMappingIndication, {
                                        accounting: mapping.accounting,
                                        display: mapping.display,
                                        purpose: "customer",
                                        size: 16
                                    }, mapping.accounting)),
                                canReachPortal ? /*#__PURE__*/ _jsx(Badge, {
                                    skin: "success",
                                    size: "small",
                                    rounded: true,
                                    outlined: true,
                                    testId: TID.ActiveBadge(),
                                    children: contactsTexts.active()
                                }) : /*#__PURE__*/ _jsx(Badge, {
                                    skin: "default",
                                    size: "small",
                                    rounded: true,
                                    outlined: true,
                                    testId: TID.NoPortalAccessBadge(),
                                    children: contactsTexts.noPortalAccess()
                                })
                            ]
                        }),
                        role != null && /*#__PURE__*/ _jsx(Typography, {
                            testId: TID.Role(),
                            variant: "paragraph2",
                            weight: "light",
                            skin: "secondary",
                            children: toContactRoleLabel(roleTexts, role)
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            alignItems: "center",
                            className: styles.contactDetailRow,
                            children: [
                                /*#__PURE__*/ _jsx(Email, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "light",
                                    skin: "secondary",
                                    children: contact.email()
                                })
                            ]
                        }),
                        phone != null && phone !== '' && /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            alignItems: "center",
                            testId: TID.Phone(),
                            className: styles.contactDetailRow,
                            children: [
                                /*#__PURE__*/ _jsx(Phone, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "light",
                                    skin: "secondary",
                                    children: phone
                                })
                            ]
                        })
                    ]
                }),
                !contact.isSample() && /*#__PURE__*/ _jsxs(DropdownMenu, {
                    gap: 0,
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'client_detail_contact_menu'
                        },
                        testId: TID.Menu(),
                        skin: "transparent",
                        className: styles.kebab,
                        children: /*#__PURE__*/ _jsx(Kebab, {
                            size: 20
                        })
                    }),
                    children: [
                        !isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'client_detail_contact_edit'
                            },
                            testId: TID.MenuEdit(),
                            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                            onClick: ()=>onEdit(contact),
                            disabled: isEmailUpdateInProgress,
                            tooltipProps: isEmailUpdateInProgress ? {
                                title: contactsTexts.updateInProgress()
                            } : undefined,
                            children: contactsTexts.menu.edit()
                        }),
                        !isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'client_detail_contact_change_role'
                            },
                            testId: TID.MenuChangeRole(),
                            iconStart: /*#__PURE__*/ _jsx(UserSquare, {}),
                            onClick: ()=>onChangeRole(contact),
                            children: contactsTexts.menu.changeRole()
                        }),
                        /*#__PURE__*/ _jsx(ClientViewMenuItem, {
                            closeOnClick: true,
                            clientBusinessId: (_contact_customerId = contact.customerId()) !== null && _contact_customerId !== void 0 ? _contact_customerId : null,
                            viewUserId: (_contact_userId = contact.userId()) !== null && _contact_userId !== void 0 ? _contact_userId : undefined,
                            disabled: !canReachPortal,
                            disabledTooltip: clientViewTexts.menuItemDisabledNoCustomer(),
                            testId: TID.MenuViewPortal()
                        }),
                        !isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'client_detail_contact_delete'
                            },
                            testId: TID.MenuDelete(),
                            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                            skin: "error",
                            onClick: ()=>onDelete(contact),
                            disabled: !contact.canDelete(),
                            tooltipProps: deleteTooltipProps(contact.deleteEligibility(), deleteBlockedTexts),
                            children: contactsTexts.menu.delete()
                        })
                    ]
                })
            ]
        })
    });
}
